/**
 * 🖼️ PROFILE FRAMES - Cadres personnalisés pour photos de profil
 * 
 * Des cadres élégants, luxueux, rigolos... pour exprimer ta personnalité !
 * Parce qu'une photo dans un simple rond, c'est ENNUYEUX ! 😴
 * 
 * "Une image vaut mille mots" - Proverbe chinois 🐉
 */

import React from 'react';
import { motion } from 'framer-motion';

// 🎨 CATALOGUE DES CADRES
export const PROFILE_FRAMES = [
  // 📦 BASIQUES (Gratuits)
  { 
    id: 'none', 
    name: 'Simple', 
    emoji: '⭕', 
    category: 'basic',
    description: 'Classique et sobre',
    premium: false,
    style: {
      border: '3px solid',
      borderColor: '#6b7280',
      borderRadius: '50%',
    }
  },
  { 
    id: 'gradient_purple', 
    name: 'Violet Doux', 
    emoji: '💜', 
    category: 'basic',
    description: 'Un dégradé tout en douceur',
    premium: false,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #a855f7, #ec4899) border-box',
      borderRadius: '50%',
    }
  },
  
  // 💎 LUXE (Premium)
  { 
    id: 'gold', 
    name: 'Or Royal', 
    emoji: '👑', 
    category: 'luxe',
    description: 'Pour les VIP qui brillent !',
    premium: true,
    animated: true,
    style: {
      border: '4px solid',
      borderImage: 'linear-gradient(135deg, #ffd700, #ffec8b, #ffd700, #daa520) 1',
      borderRadius: '50%',
      boxShadow: '0 0 20px rgba(255, 215, 0, 0.5)',
    }
  },
  { 
    id: 'diamond', 
    name: 'Diamant', 
    emoji: '💎', 
    category: 'luxe',
    description: 'Tu brilles comme un diamant !',
    premium: true,
    animated: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #e0f7fa, #80deea, #4dd0e1, #00bcd4) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 25px rgba(0, 188, 212, 0.4)',
    }
  },
  { 
    id: 'platinum', 
    name: 'Platine Elite', 
    emoji: '🪙', 
    category: 'luxe',
    description: 'L\'élégance absolue',
    premium: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #e5e5e5, #c0c0c0, #a8a8a8, #c0c0c0) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 15px rgba(192, 192, 192, 0.5)',
    }
  },
  { 
    id: 'rose_gold', 
    name: 'Or Rose', 
    emoji: '🌹', 
    category: 'luxe',
    description: 'Douceur et raffinement',
    premium: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #e8b4b8, #d4a5a5, #c9968a, #e8b4b8) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 15px rgba(232, 180, 184, 0.4)',
    }
  },
  
  // 🎭 FUN & DÉLIRANTS
  { 
    id: 'neon_pink', 
    name: 'Néon Pink', 
    emoji: '💗', 
    category: 'fun',
    description: 'Disco vibes ! 🕺',
    premium: false,
    animated: true,
    style: {
      border: '4px solid #ff1493',
      borderRadius: '50%',
      boxShadow: '0 0 20px #ff1493, 0 0 40px #ff1493, inset 0 0 10px rgba(255, 20, 147, 0.2)',
    }
  },
  { 
    id: 'neon_blue', 
    name: 'Néon Cyber', 
    emoji: '💙', 
    category: 'fun',
    description: 'Mode cyberpunk activé !',
    premium: false,
    animated: true,
    style: {
      border: '4px solid #00ffff',
      borderRadius: '50%',
      boxShadow: '0 0 20px #00ffff, 0 0 40px #00ffff, inset 0 0 10px rgba(0, 255, 255, 0.2)',
    }
  },
  { 
    id: 'rainbow', 
    name: 'Arc-en-ciel', 
    emoji: '🌈', 
    category: 'fun',
    description: 'Toutes les couleurs de la vie !',
    premium: true,
    animated: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(90deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3) border-box',
      borderRadius: '50%',
    }
  },
  { 
    id: 'pixel', 
    name: 'Pixel Art', 
    emoji: '👾', 
    category: 'fun',
    description: 'Retro gaming style !',
    premium: false,
    style: {
      border: '4px solid #00ff00',
      borderRadius: '8px', // Carré arrondi pour le côté pixel
      boxShadow: '4px 4px 0 #008800',
      imageRendering: 'pixelated',
    }
  },
  { 
    id: 'comic', 
    name: 'BD Comics', 
    emoji: '💥', 
    category: 'fun',
    description: 'POW ! BAM ! Tu déchires !',
    premium: false,
    style: {
      border: '5px solid #000',
      borderRadius: '50%',
      boxShadow: '5px 5px 0 #ffcc00',
    }
  },
  
  // 🌸 ROMANTIQUES
  { 
    id: 'hearts', 
    name: 'Cœurs', 
    emoji: '💕', 
    category: 'romantic',
    description: 'L\'amour est dans l\'air...',
    premium: false,
    animated: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #ff6b6b, #ee5a5a, #ff8787, #ff6b6b) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 15px rgba(255, 107, 107, 0.4)',
    }
  },
  { 
    id: 'flowers', 
    name: 'Fleurs', 
    emoji: '🌸', 
    category: 'romantic',
    description: 'Doux comme le printemps',
    premium: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #ffb6c1, #ffc0cb, #ff69b4, #ffb6c1) border-box',
      borderRadius: '50%',
    }
  },
  { 
    id: 'heart_shape', 
    name: 'En Cœur', 
    emoji: '❤️', 
    category: 'romantic',
    description: 'Ta photo en forme de cœur !',
    premium: true,
    shape: 'heart',
    style: {
      border: '4px solid #ff6b6b',
      clipPath: 'path("M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z")',
    }
  },
  
  // 🔥 BADASS
  { 
    id: 'fire', 
    name: 'Heartbeatss', 
    emoji: '🔥', 
    category: 'badass',
    description: 'Tu mets le feu !',
    premium: true,
    animated: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(180deg, #ff4500, #ff6347, #ffa500, #ff4500) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 20px rgba(255, 69, 0, 0.6)',
    }
  },
  { 
    id: 'lightning', 
    name: 'Éclairs', 
    emoji: '⚡', 
    category: 'badass',
    description: 'Électrisant !',
    premium: true,
    animated: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #ffff00, #ffd700, #ffff00, #ffa500) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 25px rgba(255, 255, 0, 0.5)',
    }
  },
  { 
    id: 'dark_metal', 
    name: 'Métal Noir', 
    emoji: '🖤', 
    category: 'badass',
    description: 'Dark et mystérieux',
    premium: false,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #2d2d2d, #4a4a4a, #2d2d2d, #1a1a1a) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
    }
  },
  { 
    id: 'skull', 
    name: 'Tête de Mort', 
    emoji: '💀', 
    category: 'badass',
    description: 'Pour les rebelles !',
    premium: true,
    style: {
      border: '4px solid #333',
      borderRadius: '50%',
      boxShadow: '0 0 20px rgba(0, 0, 0, 0.5), inset 0 0 20px rgba(0, 0, 0, 0.3)',
    }
  },
  
  // ✨ MAGIQUES
  { 
    id: 'sparkles', 
    name: 'Paillettes', 
    emoji: '✨', 
    category: 'magic',
    description: 'Tu scintilles de partout !',
    premium: true,
    animated: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #ffd700, #fff8dc, #ffd700, #ffec8b) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 30px rgba(255, 215, 0, 0.4)',
    }
  },
  { 
    id: 'aurora', 
    name: 'Aurore Boréale', 
    emoji: '🌌', 
    category: 'magic',
    description: 'Magique comme le ciel du nord',
    premium: true,
    animated: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #00ff87, #60efff, #ff00ea, #00ff87) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 25px rgba(96, 239, 255, 0.4)',
    }
  },
  { 
    id: 'galaxy', 
    name: 'Galaxie', 
    emoji: '🌠', 
    category: 'magic',
    description: 'Perdu dans les étoiles...',
    premium: true,
    animated: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #1a1a2e, #16213e, #0f3460, #1a1a2e) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 20px rgba(15, 52, 96, 0.6)',
    }
  },
  
  // 📸 RÉTRO & VINTAGE
  { 
    id: 'polaroid', 
    name: 'Polaroid', 
    emoji: '📷', 
    category: 'retro',
    description: 'Nostalgie instantanée',
    premium: false,
    shape: 'polaroid',
    style: {
      border: '8px solid white',
      borderBottom: '30px solid white',
      borderRadius: '4px',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
    }
  },
  { 
    id: 'vintage', 
    name: 'Vintage', 
    emoji: '🎞️', 
    category: 'retro',
    description: 'Le charme d\'antan',
    premium: false,
    style: {
      border: '4px solid #8b4513',
      borderRadius: '50%',
      boxShadow: '0 0 0 4px #d2691e, 0 0 15px rgba(139, 69, 19, 0.4)',
      filter: 'sepia(20%)',
    }
  },
  { 
    id: 'film', 
    name: 'Pellicule', 
    emoji: '🎬', 
    category: 'retro',
    description: 'Comme au cinéma !',
    premium: true,
    shape: 'film',
    style: {
      border: '6px solid #1a1a1a',
      borderRadius: '4px',
    }
  },
  
  // 🌱 PASSIONS - Nature & Terre
  { 
    id: 'nature_leaves', 
    name: 'Feuillage', 
    emoji: '🌿', 
    category: 'passion',
    passion: 'nature',
    description: 'Pour les amoureux de la Terre',
    premium: false,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #22c55e, #16a34a, #15803d, #22c55e) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 15px rgba(34, 197, 94, 0.4)',
    }
  },
  { 
    id: 'nature_forest', 
    name: 'Forêt Enchantée', 
    emoji: '🌲', 
    category: 'passion',
    passion: 'nature',
    description: 'Bain de forêt',
    premium: true,
    animated: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #166534, #15803d, #22c55e, #166534) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 20px rgba(22, 101, 52, 0.5)',
    }
  },
  
  // 🧘 PASSIONS - Spiritualité
  { 
    id: 'zen_mandala', 
    name: 'Mandala Zen', 
    emoji: '🕉️', 
    category: 'passion',
    passion: 'spirituality',
    description: 'Paix intérieure',
    premium: true,
    animated: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #a855f7, #7c3aed, #6d28d9, #a855f7) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 25px rgba(168, 85, 247, 0.4)',
    }
  },
  { 
    id: 'positive_energy', 
    name: 'Énergie Positive', 
    emoji: '☀️', 
    category: 'passion',
    passion: 'spirituality',
    description: 'Rayonne de bonnes ondes',
    premium: false,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #fbbf24, #f59e0b, #d97706, #fbbf24) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 20px rgba(251, 191, 36, 0.5)',
    }
  },
  
  // 📚 PASSIONS - Savoir
  { 
    id: 'book_lover', 
    name: 'Rat de Bibliothèque', 
    emoji: '📚', 
    category: 'passion',
    passion: 'knowledge',
    description: 'Dévoreur de livres',
    premium: false,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #b45309, #92400e, #78350f, #b45309) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 15px rgba(180, 83, 9, 0.4)',
    }
  },
  { 
    id: 'philosopher', 
    name: 'Penseur', 
    emoji: '🤔', 
    category: 'passion',
    passion: 'knowledge',
    description: 'Questionne l\'univers',
    premium: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #0369a1, #0284c7, #0ea5e9, #0369a1) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 15px rgba(3, 105, 161, 0.4)',
    }
  },
  
  // 🍹 PASSIONS - Sorties & Fêtes
  { 
    id: 'party_neon', 
    name: 'Fêtard', 
    emoji: '🪩', 
    category: 'passion',
    passion: 'nightlife',
    description: 'La nuit t\'appartient !',
    premium: false,
    animated: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #d946ef, #c026d3, #a21caf, #d946ef) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 25px rgba(217, 70, 239, 0.5), 0 0 50px rgba(192, 38, 211, 0.3)',
    }
  },
  { 
    id: 'cocktail', 
    name: 'Cocktail Vibes', 
    emoji: '🍹', 
    category: 'passion',
    passion: 'nightlife',
    description: 'Cheers !',
    premium: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #f472b6, #ec4899, #db2777, #f472b6) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 15px rgba(244, 114, 182, 0.4)',
    }
  },
  
  // 🎨 PASSIONS - Créativité
  { 
    id: 'artist_palette', 
    name: 'Artiste', 
    emoji: '🎨', 
    category: 'passion',
    passion: 'creativity',
    description: 'Âme créative',
    premium: false,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(90deg, #ef4444, #f97316, #eab308, #22c55e, #3b82f6, #8b5cf6) border-box',
      borderRadius: '50%',
    }
  },
  { 
    id: 'music_notes', 
    name: 'Mélomane', 
    emoji: '🎵', 
    category: 'passion',
    passion: 'creativity',
    description: 'La musique dans l\'âme',
    premium: true,
    animated: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #4f46e5, #6366f1, #818cf8, #4f46e5) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 20px rgba(99, 102, 241, 0.4)',
    }
  },
  
  // 🏔️ PASSIONS - Aventure
  { 
    id: 'adventure_mountain', 
    name: 'Aventurier', 
    emoji: '🏔️', 
    category: 'passion',
    passion: 'adventure',
    description: 'L\'appel du sommet',
    premium: false,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #0891b2, #06b6d4, #22d3ee, #0891b2) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 15px rgba(8, 145, 178, 0.4)',
    }
  },
  { 
    id: 'traveler', 
    name: 'Globe-Trotter', 
    emoji: '✈️', 
    category: 'passion',
    passion: 'adventure',
    description: 'Citoyen du monde',
    premium: true,
    style: {
      border: '4px solid transparent',
      background: 'linear-gradient(white, white) padding-box, linear-gradient(135deg, #0284c7, #0ea5e9, #38bdf8, #0284c7) border-box',
      borderRadius: '50%',
      boxShadow: '0 0 15px rgba(2, 132, 199, 0.4)',
    }
  },
];

// Catégories pour l'affichage
export const FRAME_CATEGORIES = [
  { id: 'basic', name: '📦 Basiques', description: 'Gratuits pour tous' },
  { id: 'luxe', name: '💎 Luxe', description: 'Pour briller !' },
  { id: 'fun', name: '🎭 Fun', description: 'Montre ta personnalité !' },
  { id: 'romantic', name: '🌸 Romantique', description: 'L\'amour avant tout' },
  { id: 'badass', name: '🔥 Badass', description: 'Pour les rebelles' },
  { id: 'magic', name: '✨ Magique', description: 'Féérique !' },
  { id: 'retro', name: '📸 Rétro', description: 'Nostalgie' },
  { id: 'passion', name: '💜 Passions', description: 'Montre ce qui te fait vibrer !' },
];

// 🎨 Composant d'affichage du cadre
export const ProfileFrame = ({ 
  src, 
  emoji,  // Support emoji fallback
  frameId = 'none', 
  size = 80, 
  alt = 'Photo de profil',
  onClick,
  className = '',
  showBadge = false,
  badgeText = '',
}) => {
  const frame = PROFILE_FRAMES.find(f => f.id === frameId) || PROFILE_FRAMES[0];
  
  // Animation pour les cadres animés
  const animationVariants = frame.animated ? {
    animate: {
      boxShadow: [
        frame.style.boxShadow,
        frame.style.boxShadow?.replace(/[\d.]+(?=px)/, (m) => parseFloat(m) * 1.3),
        frame.style.boxShadow,
      ],
    },
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    }
  } : {};
  
  // Contenu de l'image ou de l'emoji
  const renderContent = () => {
    if (src) {
      return (
        <img
          src={src}
          alt={alt}
          className="w-full h-full object-cover"
          style={{ 
            borderRadius: frame.style.borderRadius === '50%' ? '50%' : frame.style.borderRadius,
          }}
        />
      );
    } else if (emoji) {
      return (
        <div 
          className="w-full h-full flex items-center justify-center bg-gradient-to-br from-purple-400 to-pink-400"
          style={{ 
            borderRadius: frame.style.borderRadius === '50%' ? '50%' : frame.style.borderRadius,
          }}
        >
          <span style={{ fontSize: size * 0.5 }}>{emoji}</span>
        </div>
      );
    } else {
      return (
        <div 
          className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-400 to-gray-500"
          style={{ 
            borderRadius: frame.style.borderRadius === '50%' ? '50%' : frame.style.borderRadius,
          }}
        >
          <span style={{ fontSize: size * 0.3 }}>📷</span>
        </div>
      );
    }
  };
  
  // Style spécial pour Polaroid
  if (frame.shape === 'polaroid') {
    return (
      <motion.div
        className={`relative cursor-pointer ${className}`}
        style={{
          width: size,
          height: size * 1.2,
          ...frame.style,
        }}
        whileHover={{ scale: 1.05, rotate: [-2, 2, 0] }}
        onClick={onClick}
      >
        {src ? (
          <img
            src={src}
            alt={alt}
            className="w-full object-cover"
            style={{ height: size - 16 }}
          />
        ) : emoji ? (
          <div className="w-full flex items-center justify-center bg-gradient-to-br from-purple-400 to-pink-400" style={{ height: size - 16 }}>
            <span style={{ fontSize: size * 0.4 }}>{emoji}</span>
          </div>
        ) : null}
        {showBadge && badgeText && (
          <div className="absolute bottom-2 left-0 right-0 text-center text-xs font-handwriting text-gray-600">
            {badgeText}
          </div>
        )}
      </motion.div>
    );
  }
  
  return (
    <motion.div
      className={`relative cursor-pointer ${className}`}
      style={{
        width: size,
        height: size,
        ...frame.style,
        overflow: 'hidden',
      }}
      {...animationVariants}
      whileHover={{ scale: 1.05 }}
      onClick={onClick}
    >
      {renderContent()}
      
      {/* Badge optionnel */}
      {showBadge && badgeText && (
        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 text-white text-xs font-bold whitespace-nowrap">
          {badgeText}
        </div>
      )}
    </motion.div>
  );
};

// 🎨 Sélecteur de cadres
export const FrameSelector = ({ 
  currentFrame = 'none', 
  onSelect, 
  isDarkMode = true,
  showPremiumLock = true,
  userHasPremium = false,
}) => {
  const [selectedCategory, setSelectedCategory] = React.useState('basic');
  
  const filteredFrames = PROFILE_FRAMES.filter(f => f.category === selectedCategory);
  
  return (
    <div className="space-y-4">
      {/* Catégories */}
      <div className="flex flex-wrap gap-2">
        {FRAME_CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            onClick={() => setSelectedCategory(cat.id)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              selectedCategory === cat.id
                ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                : isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {cat.name}
          </button>
        ))}
      </div>
      
      {/* Description de la catégorie */}
      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
        {FRAME_CATEGORIES.find(c => c.id === selectedCategory)?.description}
      </p>
      
      {/* Grille des cadres */}
      <div className="grid grid-cols-4 gap-3">
        {filteredFrames.map((frame) => {
          const isLocked = frame.premium && showPremiumLock && !userHasPremium;
          const isSelected = currentFrame === frame.id;
          
          return (
            <motion.button
              key={frame.id}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => !isLocked && onSelect(frame.id)}
              className={`relative p-2 rounded-xl transition-all ${
                isSelected
                  ? 'ring-2 ring-purple-500 ring-offset-2'
                  : ''
              } ${
                isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-gray-100 hover:bg-gray-200'
              } ${isLocked ? 'opacity-60' : ''}`}
            >
              {/* Aperçu du cadre */}
              <div 
                className="w-12 h-12 mx-auto mb-1 flex items-center justify-center text-2xl"
                style={{
                  ...frame.style,
                  width: 48,
                  height: 48,
                }}
              >
                {frame.emoji}
              </div>
              
              {/* Nom */}
              <p className={`text-xs text-center truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {frame.name}
              </p>
              
              {/* Badge Premium */}
              {frame.premium && (
                <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 flex items-center justify-center">
                  <span className="text-xs">👑</span>
                </div>
              )}
              
              {/* Lock overlay */}
              {isLocked && (
                <div className="absolute inset-0 rounded-xl bg-black/40 flex items-center justify-center">
                  <span className="text-lg">🔒</span>
                </div>
              )}
              
              {/* Checkmark si sélectionné */}
              {isSelected && (
                <div className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                  <span className="text-xs text-white">✓</span>
                </div>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
};

// 🎨 Preview avec différents cadres
export const FramePreviewGrid = ({ src, isDarkMode = true }) => {
  const previewFrames = ['gold', 'neon_pink', 'fire', 'rainbow', 'aurora', 'polaroid'];
  
  return (
    <div className="grid grid-cols-3 gap-4">
      {previewFrames.map((frameId) => {
        const frame = PROFILE_FRAMES.find(f => f.id === frameId);
        return (
          <div key={frameId} className="text-center">
            <ProfileFrame
              src={src}
              frameId={frameId}
              size={60}
              className="mx-auto mb-1"
            />
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {frame?.emoji} {frame?.name}
            </p>
          </div>
        );
      })}
    </div>
  );
};

export default ProfileFrame;
